import React, { memo } from "react";
import { GenieAvatar } from "@/components/GenieAvatar";
import { getAvatarHaloBackground } from "@/lib/avatarBackground";
import { useVoiceAgent } from "@/hooks/voice/useVoiceAgent";
import { Loader2, Play } from "lucide-react";
import type { VoiceAgentAvatarProps } from "@/types/voice/voice-agent-avatar";

const VoiceAgentAvatar = memo(
  ({
    agentId,
    brandColor,
    size,
    customBranding,
    onClick,
    className = "",
    speaking,
    listening,
    thinking,
    processing,
    searching,
    retrieving,
    isConnecting,
    connected,
    embedded = false,
    coverImageUrl,
    showLogoOverlay,
    sizeScale = 1,
    integratedStart = false,
  }: VoiceAgentAvatarProps) => {
    // If voice states aren't provided, try to get them from the voice agent
    const voiceAgent = useVoiceAgent(agentId);

    const finalSpeaking = speaking ?? voiceAgent.speaking;
    const finalListening = listening ?? voiceAgent.listening;
    const finalThinking = thinking ?? voiceAgent.thinking;
    const finalProcessing = processing ?? voiceAgent.processing;
    const finalSearching = searching ?? voiceAgent.searching;
    const finalRetrieving = retrieving ?? voiceAgent.retrieving;
    const finalIsConnecting = isConnecting ?? voiceAgent.isConnecting;
    const finalConnected = connected ?? voiceAgent.connected;

    /* Live agent page renders a 400px wrapper with a 320px GenieAvatar inside
       (ratio 0.8). The character has comfortable padding inside the circle.
       In embed mode the wrapper is smaller (220px) but GenieAvatar still
       defaults to 320px — overflowing the wrapper by 50px each side and
       creating the "egg shape" visual. We fix it by holding the same 0.8 ratio:
       wrapper 220 → inner 176, wrapper 240 → inner 192. Same proportions as
       the live agent page, just smaller. */
    const EMBED_WRAPPER_PX = Math.round(220 * sizeScale);
    const EMBED_WRAPPER_MAX_PX = Math.round(240 * sizeScale);
    const STANDALONE_WRAPPER_PX = Math.round(400 * sizeScale);
    const INNER_RATIO = 0.8;
    const scaledSize = size ? Math.round(size * sizeScale) : undefined;

    const wrapperPxForInner = scaledSize ?? (embedded ? EMBED_WRAPPER_PX : null);
    const innerAvatarSize = wrapperPxForInner
      ? Math.round(wrapperPxForInner * INNER_RATIO)
      : undefined;

    const responsive = scaledSize
      ? {
          width: `${scaledSize}px`,
          height: `${scaledSize}px`,
          maxWidth: `${scaledSize}px`,
          maxHeight: `${scaledSize}px`,
        }
      : embedded
      ? {
          width: `min(${EMBED_WRAPPER_PX}px, ${Math.round(56 * sizeScale)}vw)`,
          height: `min(${EMBED_WRAPPER_PX}px, ${Math.round(56 * sizeScale)}vw)`,
          maxWidth: `${EMBED_WRAPPER_MAX_PX}px`,
          maxHeight: `${EMBED_WRAPPER_MAX_PX}px`,
        }
      : {
          width: `min(${STANDALONE_WRAPPER_PX}px, ${Math.round(80 * sizeScale)}vw)`,
          height: `min(${STANDALONE_WRAPPER_PX}px, ${Math.round(80 * sizeScale)}vw)`,
          maxWidth: `${STANDALONE_WRAPPER_PX}px`,
          maxHeight: `${STANDALONE_WRAPPER_PX}px`,
        };

    const avatarWrapperStyle: React.CSSProperties = {
      ...responsive,
      position: "relative",
      borderRadius: "50%",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      "--glow": brandColor,
      "--start-glow": `${brandColor}30`,
      cursor: integratedStart ? "default" : finalConnected ? "pointer" : finalIsConnecting ? "default" : "pointer",
      animation: finalSpeaking ? "genieGlow 1.4s ease-in-out infinite" : undefined,
      background: getAvatarHaloBackground(brandColor, customBranding?.avatarBackgroundOpacity),
      transition: "background 200ms",
      boxShadow: finalSpeaking ? `0 0 30px ${brandColor}40` : `0 0 15px ${brandColor}20`,
    } as React.CSSProperties;

    return (
      <div style={avatarWrapperStyle} onClick={!integratedStart && !finalIsConnecting ? onClick : undefined} className={className}>
        <GenieAvatar
          color={brandColor}
          secondaryColor={customBranding?.secondaryColor}
          gradientEnabled={customBranding?.gradientEnabled}
          gradientAngle={customBranding?.gradientAngle}
          avatarBackgroundOpacity={customBranding?.avatarBackgroundOpacity}
          speaking={finalSpeaking}
          listening={finalListening}
          thinking={finalThinking}
          processing={finalProcessing}
          searching={finalSearching}
          retrieving={finalRetrieving}
          isConnecting={finalIsConnecting}
          customAvatarUrl={customBranding?.logoUrl || customBranding?.customAvatarUrl}
          coverImageUrl={coverImageUrl}
          showLogoOverlay={showLogoOverlay}
          {...(innerAvatarSize ? { size: innerAvatarSize } : scaledSize ? { size: scaledSize } : {})}
        />
        {integratedStart && !finalConnected && onClick && (
          <button
            type="button"
            onClick={onClick}
            disabled={finalIsConnecting}
            aria-label={finalIsConnecting ? "Connecting to the Genie" : "Start voice conversation"}
            aria-busy={finalIsConnecting}
            title={finalIsConnecting ? "Connecting to the Genie" : "Start voice conversation"}
            className="group absolute inset-0 z-10 rounded-full border-0 bg-transparent transition-shadow hover:shadow-[0_0_24px_var(--start-glow)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-current focus-visible:ring-offset-4 disabled:cursor-wait"
            style={{ color: brandColor }}
          >
            <span className="pointer-events-none absolute bottom-[14%] left-1/2 -translate-x-1/2 opacity-60 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100 sm:bottom-[18%]">
              {finalIsConnecting ? (
                <Loader2 size={22} aria-hidden="true" className="animate-spin motion-reduce:animate-none" />
              ) : (
                <Play size={22} fill="currentColor" strokeWidth={1.5} aria-hidden="true" />
              )}
            </span>
          </button>
        )}
      </div>
    );
  }
);

export default VoiceAgentAvatar;
